/**
 * HTTP客户端
 * 基于Axios的统一HTTP请求客户端
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG, HTTP_STATUS, ERROR_MESSAGES } from './config';
import { ApiResponse, ApiError } from './types';

class HttpClient {
  private instance: AxiosInstance;
  private requestQueue: Map<string, AbortController> = new Map();

  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
      // 重要：支持跨域请求携带凭证（Cookie、Authorization header等）
      withCredentials: true,
      // 启用自动JSON转换
      transformResponse: [(data) => {
        if (typeof data === 'string') {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      }],
    });

    this.setupInterceptors();
  }

  /**
   * 设置请求和响应拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 添加请求ID用于取消重复请求
        const requestId = this.generateRequestId(config);
        (config as any).metadata = { requestId };

        // 添加认证token
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 显示加载状态
        if ((config as any).showLoading !== false) {
          this.showLoading();
        }

        return config;
      },
      (error) => {
        this.hideLoading();
        return Promise.reject(this.handleError(error));
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        this.hideLoading();
        return this.handleResponse(response) as any;
      },
      (error: AxiosError) => {
        this.hideLoading();
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * 处理响应
   */
  private handleResponse(response: AxiosResponse): ApiResponse {
    const { data } = response;
    
    // 如果响应数据没有code字段，说明是非标准响应格式
    if (typeof data !== 'object' || data === null) {
      return {
        code: HTTP_STATUS.OK,
        message: 'success',
        data: data,
        success: true,
        timestamp: Date.now(),
      };
    }
    
    // 标准响应格式检查
    if (data.code !== undefined) {
      if (data.code === HTTP_STATUS.OK || data.code === 200) {
        return {
          code: data.code,
          message: data.message || 'success',
          data: data.data,
          success: data.success !== undefined ? data.success : true,
          timestamp: Date.now(),
        };
      } else {
        throw new Error(data.message || '请求失败');
      }
    }
    
    // 没有code字段，说明是直接返回数据的接口（如健康检查）
    return {
      code: HTTP_STATUS.OK,
      message: 'success',
      data: data,
      success: true,
      timestamp: Date.now(),
    };
  }

  /**
   * 处理错误
   */
  private handleError(error: any): ApiError {
    let message: string = ERROR_MESSAGES.UNKNOWN_ERROR;
    let code = 0;

    if (error.response) {
      // 服务器响应错误
      const { status, data } = error.response;
      code = status;
      
      switch (status) {
        case HTTP_STATUS.UNAUTHORIZED:
          message = ERROR_MESSAGES.UNAUTHORIZED;
          this.handleUnauthorized();
          break;
        case HTTP_STATUS.FORBIDDEN:
          message = ERROR_MESSAGES.FORBIDDEN;
          break;
        case HTTP_STATUS.NOT_FOUND:
          message = ERROR_MESSAGES.NOT_FOUND;
          break;
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          message = ERROR_MESSAGES.SERVER_ERROR;
          break;
        default:
          message = (data as any)?.message || message;
      }
    } else if (error.request) {
      // 网络错误
      message = ERROR_MESSAGES.NETWORK_ERROR;
    } else if (error.code === 'ECONNABORTED') {
      // 超时错误
      message = ERROR_MESSAGES.TIMEOUT_ERROR;
    }

    return {
      code,
      message,
      details: error,
      timestamp: Date.now(),
    };
  }

  /**
   * 处理未授权错误
   */
  private handleUnauthorized(): void {
    // 清除本地存储的认证信息
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    
    // 跳转到登录页面
    window.location.href = '/login';
  }

  /**
   * 获取认证token
   */
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * 生成请求ID
   */
  private generateRequestId(config: AxiosRequestConfig): string {
    return `${config.method}_${config.url}_${JSON.stringify(config.params || {})}`;
  }

  /**
   * 显示加载状态
   */
  private showLoading(): void {
    // 可以在这里实现全局加载状态
  }

  /**
   * 隐藏加载状态
   */
  private hideLoading(): void {
    // 可以在这里实现全局加载状态
  }

  /**
   * GET请求
   */
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.get(url, config);
  }

  /**
   * POST请求
   */
  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.post(url, data, config);
  }

  /**
   * PUT请求
   */
  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.put(url, data, config);
  }

  /**
   * DELETE请求
   */
  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.delete(url, config);
  }

  /**
   * PATCH请求
   */
  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.patch(url, data, config);
  }

  /**
   * 取消请求
   */
  public cancelRequest(requestId: string): void {
    const controller = this.requestQueue.get(requestId);
    if (controller) {
      controller.abort();
      this.requestQueue.delete(requestId);
    }
  }

  /**
   * 取消所有请求
   */
  public cancelAllRequests(): void {
    this.requestQueue.forEach(controller => controller.abort());
    this.requestQueue.clear();
  }
}

// 创建HTTP客户端实例
const httpClient = new HttpClient();

export default httpClient;
